// Provenance: repo:tds-reconciliation-portal:frontend/src/components/ResolveModal.tsx
// (ADR-0005) — restyled on the estate tokens (ADR-0010). Ids became UUID strings; the
// role gate on reason codes derives from the SAME evaluation the API enforces with
// (lib/identity `roleLabelOf` mirrors the service's compat.role_label).
//
// Maker side of the maker-checker workflow: resolve one item (with optional
// evidence) or bulk-resolve many. Conditional fields follow the reason-code
// taxonomy served by /reason-codes.
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { ApiError, postJson } from '../api/client'
import {
  uploadEvidence,
  useBulkResolveMutation,
  useReasonCodes,
  useResolveMutation,
} from '../api/resolutions'
import { MANUAL_MATCH, residualPair, visibleReasons } from '../api/resolveRules'
import type { Resolution } from '../api/types'
import { useEvaluation } from '../lib/access'
import { roleLabelOf } from '../lib/identity'
import { Btn, Modal } from './Modal'
import { Money } from './Money'
import { showToast } from './toast'

export interface ResolveTarget {
  fingerprint: string
  label: string
  amount: string
  /** residual side, when known — lets two opposite-side residuals pair */
  side?: string
}

interface Props {
  opened: boolean
  onClose: () => void
  runId: string
  periodId: string
  itemKind: 'party' | 'residual'
  targets: ResolveTarget[]
  /** Existing resolution for a single target (shows rejection feedback). */
  existing?: Resolution | null
}

const FIELD_LABELS: Record<string, string> = {
  je_reference: 'Journal entry reference',
  follow_up_date: 'Follow-up date',
}

const inputCls =
  'w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm outline-none focus:border-primary'

export function ResolveModal({ opened, onClose, runId, periodId, itemKind, targets, existing }: Props) {
  const { evaluation } = useEvaluation()
  const role = roleLabelOf(evaluation)
  const qc = useQueryClient()
  const reasonsQ = useReasonCodes()
  const resolveMut = useResolveMutation(runId)
  const bulkMut = useBulkResolveMutation(runId)
  // Two opposite-side residuals routed through the match door — the entries
  // are paired to each other, so no fingerprint is ever typed (found live in
  // the predecessor: the raw counterpart_fingerprint box invited a note and
  // saved a match against nothing / died in the claims index).
  const pair = residualPair(itemKind, targets)
  const pairMut = useMutation({
    mutationFn: (p: { residual_fingerprints: [string, string]; comment?: string }) =>
      postJson<{ pending: boolean }>(`/runs/${runId}/difference/manual-match`, p),
    onSuccess: () => {
      for (const k of ['resolutions', 'resolution-progress', 'review-queue', 'party',
        'difference', 'diff-companies', 'difference-summary', 'reconciliation', 'cleared']) {
        void qc.invalidateQueries({ queryKey: [k] })
      }
    },
  })

  const [reason, setReason] = useState<string | null>(null)
  const [comment, setComment] = useState('')
  const [details, setDetails] = useState<Record<string, string>>({})
  const [evidence, setEvidence] = useState<File | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const reasons = reasonsQ.data ?? {}
  const spec = reason ? reasons[reason] : undefined
  const bulk = targets.length > 1
  const pairMatch = reason === MANUAL_MATCH && pair !== null
  const busy = submitting || resolveMut.isPending || bulkMut.isPending || pairMut.isPending

  const selectData = visibleReasons(reasons, pair !== null).map(([code, s]) => ({
    value: code,
    label: s.label,
    disabled: !!(s.resolver_roles && !s.resolver_roles.includes(role)),
  }))

  const missingRequired =
    !reason ||
    (spec?.requires_comment && !comment.trim()) ||
    (spec?.requires ?? []).some((f) => !(details[f] ?? '').trim())

  const reset = () => {
    setReason(null)
    setComment('')
    setDetails({})
    setEvidence(null)
  }

  const submit = async () => {
    if (!reason) return
    setSubmitting(true)
    try {
      const payload = {
        reason_code: reason,
        comment: comment.trim() || undefined,
        details: Object.keys(details).length ? details : undefined,
      }
      if (pairMatch && pair) {
        const out = await pairMut.mutateAsync({
          residual_fingerprints: pair,
          comment: comment.trim() || undefined,
        })
        showToast({
          color: out.pending ? 'yellow' : 'green',
          title: out.pending
            ? 'Matched to each other — awaiting approval'
            : 'Matched to each other',
        })
        reset()
        onClose()
        return
      }
      if (bulk) {
        await bulkMut.mutateAsync({
          fingerprints: targets.map((t) => t.fingerprint),
          item_kind: itemKind,
          ...payload,
        })
      } else {
        let evidenceId: string | undefined
        if (evidence) {
          evidenceId = (await uploadEvidence(periodId, evidence)).id
        }
        await resolveMut.mutateAsync({
          fingerprint: targets[0].fingerprint,
          item_kind: itemKind,
          evidence_upload_id: evidenceId,
          ...payload,
        })
      }
      showToast({
        color: 'green',
        title: bulk
          ? `${targets.length} items sent for review`
          : 'Resolution sent for review',
      })
      reset()
      onClose()
    } catch (e) {
      showToast({
        color: 'red',
        title: 'Could not resolve',
        message: e instanceof ApiError ? e.message : String(e),
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={() => {
        reset()
        onClose()
      }}
      title={bulk ? `Resolve ${targets.length} items` : 'Resolve item'}
      wide
    >
      <div className="space-y-3">
        {existing?.status === 'open' && existing.review_comment && (
          <div className="rounded-md border border-red-300 bg-red-50 p-3">
            <p className="text-sm font-semibold text-red-900">Rejected by reviewer</p>
            <p className="mt-0.5 text-sm text-red-900">{existing.review_comment}</p>
          </div>
        )}

        <div className="space-y-1">
          {targets.slice(0, 6).map((t) => (
            <div key={t.fingerprint} className="flex items-center justify-between gap-4">
              <p className="truncate text-sm text-foreground">{t.label}</p>
              <Money v={t.amount} />
            </div>
          ))}
          {targets.length > 6 && (
            <p className="text-xs text-muted-foreground">… and {targets.length - 6} more</p>
          )}
        </div>

        <label className="block text-sm">
          <span className="mb-1 block font-medium text-foreground">Reason</span>
          <select
            className={inputCls}
            value={reason ?? ''}
            onChange={(e) => setReason(e.target.value || null)}
            required
          >
            <option value="">Pick a reason code</option>
            {selectData.map((o) => (
              <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>
            ))}
          </select>
        </label>

        {pairMatch && (
          <p className="text-sm text-muted-foreground">
            These two entries will be matched to each other — no fingerprint to
            type. The amount difference stays visible as the group&apos;s residual;
            if it exceeds the run&apos;s tolerance, the comment must say why.
          </p>
        )}

        {(spec?.requires ?? []).map((f) => (
          <label key={f} className="block text-sm">
            <span className="mb-1 block font-medium text-foreground">{FIELD_LABELS[f] ?? f}</span>
            <input
              className={inputCls}
              type={f === 'follow_up_date' ? 'date' : 'text'}
              value={details[f] ?? ''}
              onChange={(e) => setDetails({ ...details, [f]: e.target.value })}
              required
            />
          </label>
        ))}

        <label className="block text-sm">
          <span className="mb-1 block font-medium text-foreground">
            {spec?.requires_comment ? 'Comment (required)' : 'Comment'}
          </span>
          <textarea
            className={`${inputCls} min-h-16`}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            required={spec?.requires_comment}
          />
        </label>

        {!bulk && (
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-foreground">Evidence (optional)</span>
            <input
              className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border file:border-border file:bg-background file:px-3 file:py-1.5 file:text-sm file:font-medium hover:file:bg-muted"
              type="file"
              accept=".png,.jpg,.jpeg,.eml,.msg,.docx,.zip,.pdf,.xlsx,.csv"
              onChange={(e) => setEvidence(e.target.files?.[0] ?? null)}
            />
            {evidence && (
              <span className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                {evidence.name}
                <button type="button" className="text-primary hover:underline"
                  onClick={() => setEvidence(null)}>
                  remove
                </button>
              </span>
            )}
          </label>
        )}

        <div className="flex justify-end gap-2">
          <Btn onClick={onClose} disabled={busy}>Cancel</Btn>
          <Btn kind="primary" onClick={() => void submit()} loading={busy}
            disabled={Boolean(missingRequired)}>
            {pairMatch ? 'Match pair' : bulk ? 'Resolve all' : 'Resolve'}
          </Btn>
        </div>
      </div>
    </Modal>
  )
}
